import { useState } from 'react';
import { motion } from 'framer-motion';
import {
    Terminal,
    Sparkles,
    Bot,
    Brain,
    Check,
    Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { InteractiveSnippet } from '@/components/common/InteractiveSnippet';

const PROMPTS = [
    {
        id: 'chatgpt',
        name: 'ChatGPT',
        icon: Bot,
        description: 'Great for general web development queries.',
        prompt: `I have a website and I need to install the ChytBot widget.
Here is the installation script:

<script 
  src="https://chytbot.com/loader.js" 
  data-bot-id="YOUR_BOT_ID" 
  defer
></script>

Please explain where I should paste this code in my project files to make the chatbot appear on every page. If I am using a specific framework (like React, Vue, or simple HTML), please adapt the instructions accordingly.`
    },
    {
        id: 'claude',
        name: 'Claude',
        icon: Brain,
        description: 'Excellent for complex integration instructions.',
        prompt: `I need to add the ChytBot customer support widget to my application.
The widget code is:

<script 
  src="https://chytbot.com/loader.js" 
  data-bot-id="YOUR_BOT_ID" 
  defer
></script>

Please analyze my codebase (or ask me about my tech stack) and determine the optimal location for this script. I want to ensure it loads efficiently. Provide the implementation code.`
    },
    {
        id: 'cursor',
        name: 'Cursor / Windsurf',
        icon: Terminal,
        description: 'Best for IDE-integrated AI coding assistants.',
        prompt: `I need to integrate the ChytBot chat widget into this application.
Here is the script I need to add:

<script 
  src="https://chytbot.com/loader.js" 
  data-bot-id="YOUR_BOT_ID" 
  defer
></script>

Please analyze my project structure and determine the best place to add this script tag so that it loads on every page (e.g., index.html, root layout, or main app file). 
If this is a React/Next.js/Vue app, please implement it using best practices for that framework (useEffect, next/script, etc.).`
    },
    {
        id: 'v0',
        name: 'v0 / Bolt.new',
        icon: Sparkles,
        description: 'Best for generating new UI or components.',
        prompt: `I am building a website and I want to include a customer support chatbot.
Please add the following separate script to the main HTML or layout of the generated code:

<script 
  src="https://chytbot.com/loader.js" 
  data-bot-id="YOUR_BOT_ID" 
  defer
></script>

Make sure the widget is loaded and visible.`
    }
];

// Renders prompt text with a clickable YOUR_BOT_ID badge via InteractiveSnippet
const HighlightedPrompt = ({ text, isDashboard }: { text: string; isDashboard: boolean }) => {
    return (
        <div className="font-mono text-xs leading-relaxed text-foreground/75 bg-muted/30 rounded-xl p-4 border border-border/40 whitespace-pre-wrap break-words">
            <InteractiveSnippet text={text} isDashboard={isDashboard} />
        </div>
    );
};

const PromptCard = ({ item, index, isDashboard }: { item: typeof PROMPTS[0]; index: number; isDashboard: boolean }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(item.prompt);
        setCopied(true);
        toast.success('Prompt copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.08 }}
            className="bg-card border border-border rounded-2xl"
        >
            {/* Card Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/60 bg-muted/20 rounded-t-2xl">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        <item.icon className="w-4 h-4" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-sm text-foreground">{item.name}</h3>
                        <p className="text-xs text-muted-foreground">{item.description}</p>
                    </div>
                </div>
                <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-background hover:bg-muted/50 transition-colors text-xs font-medium text-muted-foreground hover:text-foreground"
                >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>

            {/* Prompt Body */}
            <div className="p-5 rounded-b-2xl">
                <HighlightedPrompt text={item.prompt} isDashboard={isDashboard} />
            </div>
        </motion.div>
    );
};

export const AiImplementationList = ({ isDashboard = false }: { isDashboard?: boolean }) => {
    return (
        <div className="space-y-4">
            {PROMPTS.map((item, index) => (
                <PromptCard key={item.id} item={item} index={index} isDashboard={isDashboard} />
            ))}
        </div>
    );
};
